import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import AuditCta from "@/components/AuditCta";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { PlatformMachineStill } from "@/components/cinematic/stills";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function PlatformPage() {
  const [modules, setModules] = useState([]);
  useEffect(() => {
    api.get("/config/modules").then((r) => setModules(r.data)).catch(() => setModules([]));
  }, []);

  return (
    <>
      <PublicNav />
      <div className="relative dt6-cinematic-vignette" style={{ minHeight: "560px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/PlatformMachine")}
          stillSrc={PlatformMachineStill}
          testId="platform-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section className="pt-16 pb-12 relative dt6-cinematic-copy" testId="platform-hero">
          <div className="dt6-chip dt6-chip-primary mb-4">/ THE FULL-STACK STORY</div>
          <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight max-w-4xl">
            The right side keeps <span style={{ color: "var(--dt6-primary)" }}>renting</span> capability. DT6 owns it.
          </h1>
          <p className="mt-6 text-lg text-[color:var(--dt6-muted)] max-w-3xl">
            Most conservative operators stitch together seven vendors, three CRMs, and a spreadsheet.
            Data leaks between systems. Compliance posture is inconsistent. The other side has an integrated stack.
            DT6 gives our side one, too — built for movement-scale operations.
          </p>
        </Section>
      </div>

      <Section className="py-12" testId="platform-problem">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="dt6-card p-8">
            <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-danger)]">/ THE PROBLEM</div>
            <h2 className="font-heading text-2xl font-bold mt-3">Fragmentation is a tax.</h2>
            <ul className="mt-4 space-y-3 text-sm text-[color:var(--dt6-muted)]">
              <li>· Voter file from one vendor, hygiene from another, appends from a third.</li>
              <li>· CRM enrichment happens off-platform. Nothing rejoins the universe.</li>
              <li>· Scoring lives in a data-science silo. Nobody uses it in the walk-list.</li>
              <li>· Lead exchange runs through spreadsheets and email threads.</li>
              <li>· Activation happens on ad platforms without hygiene, without cell flag.</li>
            </ul>
          </div>
          <div className="dt6-card p-8">
            <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-primary)]">/ THE STACK</div>
            <h2 className="font-heading text-2xl font-bold mt-3">One integrated operating system.</h2>
            <ul className="mt-4 space-y-3 text-sm text-[color:var(--dt6-muted)]">
              <li>· D1 sources the universe. D2 cleans it. D3 fills in the identity.</li>
              <li>· D4 scores every record. D5 routes qualified leads. D6 activates.</li>
              <li>· Every step logged, every hand-off audited, every deliverable versioned.</li>
              <li>· Config-driven, cycle-licensed, partner-friendly.</li>
              <li>· Built by operators, for operators.</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section className="py-12" testId="platform-modules-list">
        <div className="dt6-chip mb-4">/ THE SIX MODULES</div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">D1 through D6, in sequence.</h2>
        <div className="space-y-3">
          {modules.map((m) => (
            <Link
              key={m.code}
              to={`/modules/${m.code}`}
              className="dt6-card p-6 flex items-start justify-between gap-6 hover:border-[color:var(--dt6-primary)] transition-colors duration-150"
              data-testid={`platform-module-row-${m.code}`}
            >
              <div className="flex items-start gap-6">
                <div className="font-mono text-[0.85rem] tracking-[0.18em] text-[color:var(--dt6-primary)] pt-1">
                  {m.code}
                </div>
                <div>
                  <div className="font-heading text-xl font-bold">{m.name} — <span className="text-[color:var(--dt6-muted)] font-normal">{m.tagline}</span></div>
                  <p className="mt-1 text-sm text-[color:var(--dt6-muted)] max-w-3xl">{m.what_it_does}</p>
                </div>
              </div>
              <ArrowRight size={18} strokeWidth={1.5} className="mt-1 shrink-0 text-[color:var(--dt6-muted)]" />
            </Link>
          ))}
        </div>
      </Section>

      <Section className="py-16" testId="platform-audit-cta">
        <AuditCta context="platform-audit-cta" />
      </Section>

      <PublicFooter />
    </>
  );
}
